'use client';

import Image from 'next/image';
import { type JSX, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon, Text } from '@videoblocks/design-system';

const IMAGE_BASE = '/assets/images/conv-search';
const IMAGES = {
    plasticBag: `${IMAGE_BASE}/plastic-bag-floating-in-clear-water-in-the-sea-ecological-problem-SBI-349425855.webp`,
    desertRoad: `${IMAGE_BASE}/aerial-abstract-view-of-road-covered-by-sand-in-the-desert-abu-dhabi-uae.webp`,
    nuclearPlant: `${IMAGE_BASE}/rising-above-abandoned-nuclear-power-plant-SBI-351076464.webp`,
    floods: `${IMAGE_BASE}/floods-in-the-streams-wide-dirty-river-with-muddy-orange-water-and-fields-flooded.webp`,
    powerStation: `${IMAGE_BASE}/aerial-view-on-the-working-power-station-cooling-tower-of-nuclear-power-plant.webp`,
} as const;

interface ValueProp {
    title: string;
    description: string;
}

const VALUE_PROPS: ValueProp[] = [
    {
        title: 'Natural language search',
        description: 'Describe what you need in your own words, no keywords required.',
    },
    {
        title: 'Precision & accuracy',
        description: 'Tell us what to leave out and you can find content that matches your vision.',
    },
    {
        title: 'Refine through conversation',
        description: 'Chat to narrow results without having to start over.',
    },
];

/**
 * Card 1 illustration: two overlapping stock photos with a centered prompt card.
 */
const NaturalLanguageIllustration = (): JSX.Element => (
    <>
        <div className="absolute left-16 top-8 size-75 overflow-hidden rounded-3xl shadow-lg">
            <Image src={IMAGES.plasticBag} alt="Plastic bag floating in clear water" fill className="object-cover" />
        </div>
        <div className="absolute left-61 top-22 size-75 overflow-hidden rounded-3xl shadow-lg">
            <Image
                src={IMAGES.desertRoad}
                alt="Aerial view of desert road covered by sand"
                fill
                className="object-cover"
            />
        </div>

        {/* Centered prompt card */}
        <div className="absolute left-1/2 top-1/2 w-68 -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white px-6 py-8 shadow-lg">
            <p className="text-sm font-medium leading-relaxed text-gray-900">
                I&rsquo;m making a short documentary about the impacts of ecological disasters on small towns. Tone is
                somber but factual.
            </p>
        </div>
    </>
);

/**
 * Card 2 illustration: scattered faded thumbnails with a centered "Thinking" pill
 * with a bubblegum glow effect.
 */
const precisionIllustrationThumbnails = [
    {
        src: IMAGES.plasticBag,
        className: 'left-12 top-19 h-40 w-33',
    },
    {
        src: IMAGES.desertRoad,
        className: 'left-52 top-9 h-30 w-52',
    },
    {
        src: IMAGES.nuclearPlant,
        className: 'left-78 top-59 h-30 w-48',
    },
    {
        src: IMAGES.floods,
        className: 'left-19 top-66 h-30 w-52',
    },
    {
        src: IMAGES.powerStation,
        className: 'right-3 top-13 h-40 w-32',
    },
];

const PrecisionIllustration = (): JSX.Element => (
    <>
        {precisionIllustrationThumbnails.map((thumb) => (
            <div
                key={thumb.src}
                className={twMerge('absolute overflow-hidden rounded-2xl opacity-50', thumb.className)}
            >
                <Image src={thumb.src} alt="" fill className="object-cover" aria-hidden="true" />
            </div>
        ))}

        {/* Centered "Thinking" pill with bubblegum glow */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {/* Glow effect */}
            <div className="absolute -inset-3 rounded-full bg-bubblegum-200 opacity-60 blur-xl" aria-hidden="true" />
            <div className="relative flex items-center gap-2 rounded-full bg-linear-to-b from-white to-gray-200 px-5 py-2.5">
                <Icon name="brain" className="h-4 w-4 shrink-0 [&_path]:fill-gray-500" />
                <span className="text-sm text-gray-500 pointer-events-none">Thinking</span>
                <Icon name="chevronDown" className="h-3 w-3 shrink-0 [&_path]:fill-gray-500" />
            </div>
        </div>
    </>
);

/**
 * Card 3 illustration: a grid of image thumbnails with a chat panel overlay
 * showing a conversation about refining results.
 */
const refineIllustrationGridImages = [
    IMAGES.desertRoad,
    IMAGES.plasticBag,
    IMAGES.nuclearPlant,
    IMAGES.floods,
    IMAGES.powerStation,
    IMAGES.desertRoad,
    IMAGES.nuclearPlant,
    IMAGES.plasticBag,
    IMAGES.floods,
];

const RefineIllustration = (): JSX.Element => (
    <>
        {/* Thumbnail grid */}
        <div className="absolute inset-0 grid grid-cols-3 gap-2 p-4">
            {refineIllustrationGridImages.map((src, i) => (
                <div key={`${src}-${i}`} className="relative overflow-hidden rounded-2xl">
                    <Image src={src} alt="" fill className="object-cover" aria-hidden="true" />
                </div>
            ))}
        </div>

        {/* Chat panel overlay */}
        <div className="absolute -top-6 left-0 w-96 overflow-hidden rounded-3xl border-l border-black/10 bg-white shadow-[0px_0px_24px_12px_rgba(26,28,30,0.2)]">
            {/* Conversation */}
            <div className="flex flex-col gap-3 px-6 pt-5 pb-4">
                {/* Assistant messages */}
                <div className="rounded-br-lg rounded-bl-lg rounded-tr-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-sm leading-relaxed text-gray-900 pointer-events-none">
                        I found nearly <span className="font-bold">600 pieces of footage</span> that go with the tone
                        and theme of your project
                    </p>
                </div>
                <div className="rounded-br-lg rounded-bl-lg rounded-tr-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-sm leading-relaxed text-gray-900 pointer-events-none">
                        Describe the scene you&rsquo;re working on. That way I can find the perfect piece of footage for
                        you.
                    </p>
                </div>

                {/* User message */}
                <div className="self-end rounded-bl-2xl rounded-br-2xl rounded-tl-2xl border border-gray-200 bg-white px-3.5 py-2">
                    <p className="text-sm leading-relaxed text-gray-900 pointer-events-none">
                        Video should have a single focal point that isn&rsquo;t too busy
                    </p>
                </div>
            </div>

            {/* Input footer */}
            <div className="border-t border-gray-100 bg-white px-6 pb-5 pt-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50">
                    <div className="rounded-xl border border-gray-300 bg-white px-4 py-3 shadow-sm">
                        <p className="text-sm text-gray-600 pointer-events-none">Chat to refine your results...</p>
                    </div>
                    <div className="flex items-center justify-end px-2 py-2">
                        <span className="rounded-md bg-gray-800 px-3 py-1 text-xs font-semibold text-white pointer-events-none">
                            Refine
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </>
);

const ILLUSTRATIONS = [NaturalLanguageIllustration, PrecisionIllustration, RefineIllustration];

export function ValueProps(): JSX.Element {
    const [activeIndex, setActiveIndex] = useState(0);
    const ActiveIllustration = ILLUSTRATIONS[activeIndex];

    return (
        <section className="flex flex-col items-center gap-10 bg-gray-900 px-6 py-24 lg:px-24">
            <Text asChild fontFamily="polysans" fontSize="3xl" fontWeight="semibold" className="text-white">
                <h2>Go beyond keywords to find the perfect content</h2>
            </Text>

            <div className="flex flex-col gap-8 lg:flex-row lg:gap-22 max-w-6xl">
                {/* Left illustration — changes per active card
                    TODO: Consider adding a crossfade transition when switching cards */}
                <div className="relative h-105 w-full shrink-0 overflow-hidden rounded-3xl bg-yellow-200 lg:w-152">
                    <ActiveIllustration />
                </div>

                {/* Right value prop cards */}
                <div className="flex flex-1 flex-col gap-2">
                    {VALUE_PROPS.map((prop, index) => {
                        const isActive = index === activeIndex;
                        return (
                            <button
                                key={prop.title}
                                type="button"
                                onClick={(): void => setActiveIndex(index)}
                                className={twMerge(
                                    'flex flex-col items-start rounded-3xl bg-white px-6 text-left transition-all duration-300 cursor-pointer',
                                    isActive ? 'flex-1 justify-between py-6' : 'py-8',
                                )}
                            >
                                <Text
                                    asChild
                                    fontFamily="polysans"
                                    fontSize="2xl"
                                    fontWeight="semibold"
                                    className="text-gray-900"
                                >
                                    <span>{prop.title}</span>
                                </Text>
                                {/* smoothly animate body text. active: full height, visible; inactive: zero height, hidden */}
                                <div
                                    className={twMerge(
                                        'grid transition-all duration-300',
                                        isActive ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                                    )}
                                >
                                    <div className="overflow-hidden">
                                        <Text fontSize="xl" fontWeight="regular" className="text-gray-900">
                                            {prop.description}
                                        </Text>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
